import type { Board } from './boardModel';

export interface BoardCompletion {
  /** Tasks standing in the board's Done column. */
  done: number;
  /** Every task on the board, wherever it stands. */
  total: number;
  /** `done / total`, 0 when the board is empty. */
  fraction: number;
}

/**
 * How much of a board's work is finished (FD-16's first permitted use of the
 * reserved gradient).
 *
 * **What a filled bar means, and what it used to claim here.** This started out
 * reporting the furthest phase that held any task, on the reasoning that a board
 * is a pipeline and the honest question is "where has this work reached". That is
 * true of one item moving through stages and false of a board, which is many
 * items in parallel: a single finished task sits in the last column, so the bar
 * read full while two of three tasks were untouched. One task pinned it at 100%
 * permanently. A bar that cannot go down is not measuring anything.
 *
 * So it measures what everyone already reads a filled bar as measuring: the share
 * of tasks that are done. Three tasks with one finished is one third, and it moves
 * both ways — creating work lowers it, which is correct, and was the objection
 * that talked me out of a ratio the first time.
 *
 * Done is the column `buildBoard` stamps `systemRole: 'done'`, not any step whose
 * *kind* happens to be `done`: a phase may declare its own done-kind step, and
 * only the manufactured system column means the task is finished with the
 * workflow (see `BoardStepSection`).
 */
export function boardCompletion(board: Board): BoardCompletion {
  let done = 0;
  let total = 0;

  for (const column of board.columns) {
    for (const step of column.steps) {
      total += step.tasks.length;
      if (column.systemRole === 'done') done += step.tasks.length;
    }
  }

  return { done, total, fraction: total === 0 ? 0 : done / total };
}
